import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { agentApi, pipelineApi } from '../../services/api';
import AgentResultView from '../../components/agentResults/AgentResultView';
import {
  DocumentTextIcon,
  EnvelopeIcon,
  UserGroupIcon,
  EyeIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  SparklesIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  PlayIcon,
} from 'components/icons';
import toast from 'react-hot-toast';
import PageHeader from '../../components/ui/PageHeader';

// ===========================================
// Action Definitions
// ===========================================

/** How many runs the Recent Results list shows. Analytics counts a time window instead, so the two numbers differ by design; each label says which it is. */
const RECENT_TASK_LIMIT = 20;

const ACTIONS = [
  {
    id: 'analyze',
    agent: 'contract-scout',
    name: 'Analyze Opportunity',
    description: 'Deep-dive into an RFP — extract requirements, evaluation criteria, and deliverables',
    icon: DocumentTextIcon,
    color: 'bg-blue-500',
    bgLight: 'bg-blue-50',
    textColor: 'text-blue-700',
    buildPayload: (opp: any) => ({ action: 'analyze', opportunity_id: opp.opportunityId || opp.id }),
  },
  {
    id: 'compliance',
    agent: 'compliance-agent',
    name: 'Compliance Check',
    description: 'Check your eligibility — set-aside, certifications, NAICS, and registration requirements',
    icon: ShieldCheckIcon,
    color: 'bg-green-500',
    bgLight: 'bg-green-50',
    textColor: 'text-green-700',
    buildPayload: (opp: any) => ({ opportunity_id: opp.opportunityId || opp.id, checkType: 'eligibility' }),
  },
  {
    id: 'vendor-match',
    agent: 'vendor-matcher',
    name: 'Find Teaming Partners',
    description: 'Identify subcontractors and primes to team with based on capability and past performance',
    icon: UserGroupIcon,
    color: 'bg-purple-500',
    bgLight: 'bg-purple-50',
    textColor: 'text-purple-700',
    buildPayload: (opp: any) => ({
      opportunity_id: opp.opportunityId || opp.id,
      requirements: opp.opportunity?.naicsCodes || [],
      maxResults: 10,
    }),
  },
  {
    id: 'monitor',
    agent: 'monitor-agent',
    name: 'Monitor Changes',
    description: 'Watch for amendments, deadline changes, and new Q&A postings on this solicitation',
    icon: EyeIcon,
    color: 'bg-amber-500',
    bgLight: 'bg-amber-50',
    textColor: 'text-amber-700',
    buildPayload: (opp: any) => ({
      watch_type: 'solicitation_updates',
      opportunity_id: opp.opportunityId || opp.id,
      criteria: { noticeId: opp.opportunity?.samNoticeId },
    }),
  },
  {
    id: 'draft',
    agent: 'email-agent',
    name: 'Draft Response',
    description: 'Generate a professional email — RFI response, capability statement, or teaming inquiry',
    icon: EnvelopeIcon,
    color: 'bg-rose-500',
    bgLight: 'bg-rose-50',
    textColor: 'text-rose-700',
    buildPayload: (opp: any) => ({
      action: 'draft_reply',
      context: `Regarding opportunity: ${opp.opportunity?.title}`,
      opportunity_id: opp.opportunityId || opp.id,
      tone: 'professional',
    }),
  },
  {
    id: 'market',
    agent: 'analyst-agent',
    name: 'Market Analysis',
    description: 'Analyze competition, historical win rates, pricing trends, and incumbent information',
    icon: ChartBarIcon,
    color: 'bg-indigo-500',
    bgLight: 'bg-indigo-50',
    textColor: 'text-indigo-700',
    buildPayload: (opp: any) => ({
      opportunity_id: opp.opportunityId || opp.id,
      query: `Market analysis for: ${opp.opportunity?.title}. Agency: ${opp.opportunity?.agency}. NAICS: ${opp.opportunity?.naicsCodes?.join(', ')}. Who holds this work now, what has the office paid for it, and how should we price against the record?`,
      outputFormat: 'detailed',
    }),
  },
];

// ===========================================
// Types
// ===========================================

interface PipelineOpp {
  id: string;
  opportunityId: string;
  stage: string;
  matchScore: number;
  opportunity?: {
    id: string;
    samNoticeId: string;
    title: string;
    agency?: string;
    naicsCodes?: string[];
    solicitationNumber?: string;
    responseDeadline?: string;
  };
}

// Observed runtimes, shown while a task is in flight so the wait has a frame of
// reference. Rough on purpose — better a stated range than a fake percentage.
const TYPICAL_RUNTIME: Record<string, string> = {
  'contract-scout': 'Usually takes about 2 minutes',
  'compliance-agent': 'Usually takes 1–2 minutes',
  'vendor-matcher': 'Usually takes 1–2 minutes',
  'monitor-agent': 'Usually takes under a minute',
  'email-agent': 'Usually takes under a minute',
};

interface TaskResult {
  id: string;
  agentType: string;
  status: string;
  actionId: string;
  opportunityTitle: string;
  payload?: any;
  result?: string;
  error?: string;
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
}

// ===========================================
// Component
// ===========================================

const Agents: React.FC = () => {
  const [opportunities, setOpportunities] = useState<PipelineOpp[]>([]);
  const [selectedOpp, setSelectedOpp] = useState<PipelineOpp | null>(null);
  const [loadingOpps, setLoadingOpps] = useState(true);
  const [recentTasks, setRecentTasks] = useState<TaskResult[]>([]);
  const [loadingTasks, setLoadingTasks] = useState(true);
  const [runningAction, setRunningAction] = useState<string | null>(null);
  // Drives the elapsed counters. Ticks only while a task is in flight so an
  // idle Agents page isn't re-rendering once a second for nothing.
  const [now, setNow] = useState<number>(() => Date.now());
  const [expandedResult, setExpandedResult] = useState<string | null>(null);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [healthStatus, setHealthStatus] = useState<string>('checking');

  const hasRunningTask = recentTasks.some(
    (t) => t.status === 'queued' || t.status === 'processing'
  );

  useEffect(() => {
    if (!hasRunningTask) return;
    setNow(Date.now());
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, [hasRunningTask]);

  useEffect(() => {
    fetchOpportunities();
    fetchRecentTasks();
    checkHealth();

    // Poll for task updates every 10 seconds
    const interval = setInterval(fetchRecentTasks, 10000);
    return () => clearInterval(interval);
  // Mount-once fetch. fetchOpportunities is redefined every render, so listing it would refetch in a loop.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchOpportunities = async () => {
    try {
      const response = await pipelineApi.getOpportunities();
      const data = response.data.data?.opportunities || response.data.opportunities || [];
      setOpportunities(data);
      // Auto-select first opportunity
      if (data.length > 0 && !selectedOpp) {
        setSelectedOpp(data[0]);
      }
    } catch {
      // Silently fail
    } finally {
      setLoadingOpps(false);
    }
  };

  const fetchRecentTasks = async () => {
    try {
      const response = await agentApi.getMyTasks({ limit: RECENT_TASK_LIMIT });
      const tasks = response.data.data?.tasks || response.data.tasks || [];
      setRecentTasks(tasks.map((t: any) => ({
        id: t.id,
        agentType: t.agentType,
        status: t.status,
        actionId: ACTIONS.find(a => a.agent === t.agentType)?.id || t.agentType,
        opportunityTitle: t.payload?.opportunity_title || t.agentType,
        payload: t.payload,
        result: t.result,
        error: t.error,
        createdAt: t.createdAt,
        completedAt: t.completedAt,
      })));
    } catch {
      // Silently fail
    } finally {
      setLoadingTasks(false);
    }
  };

  const checkHealth = async () => {
    try {
      const response = await agentApi.getHealth();
      const data = response.data.data || response.data;
      // Read the engine that actually serves agent calls.
      const status = data.engine?.status || data.harness?.status || 'unavailable';
      setHealthStatus(status);
    } catch {
      setHealthStatus('unavailable');
    }
  };

  const runAction = async (action: typeof ACTIONS[0]) => {
    if (!selectedOpp) {
      toast.error('Select an opportunity from your pipeline first');
      return;
    }

    setRunningAction(action.id);
    try {
      const basePayload = action.buildPayload(selectedOpp);
      const payload = { ...basePayload, opportunity_title: selectedOpp.opportunity?.title || 'Unknown' };

      console.log('Running action:', action.agent, 'payload:', payload);

      const response = await agentApi.createTask({
        agent_type: action.agent,
        task: payload,
        priority: 'normal',
      });

      console.log('Task created:', response.data);
      const taskId = response.data.data?.task_id || response.data.task_id || response.data.data?.task?.id;
      toast.success(`${action.name} started — processing...`);

      // Add to recent tasks immediately
      setRecentTasks(prev => [{
        id: taskId,
        agentType: action.agent,
        status: 'queued',
        actionId: action.id,
        opportunityTitle: selectedOpp.opportunity?.title || 'Unknown',
        createdAt: new Date().toISOString(),
      }, ...prev]);

      // Poll for completion — keeps runningAction set until done
      pollTask(taskId, action).finally(() => setRunningAction(null));
    } catch (error: any) {
      console.error('Action error:', action.agent, error.response?.status, error.response?.data, error.message);
      const msg = error.response?.data?.error?.message || error.response?.data?.error || error.message || 'Failed to start action';
      toast.error(typeof msg === 'string' ? msg : 'Failed to start action');
      setRunningAction(null);
    }
  };

  const pollTask = async (taskId: string | undefined, action: typeof ACTIONS[0]) => {
    if (!taskId) return;
    const maxAttempts = 30;
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise(r => setTimeout(r, 3000));
      try {
        const response = await agentApi.getTask(taskId);
        const task = response.data.data?.task || response.data.task || response.data;
        const status = task.status;

        // Update in the list
        setRecentTasks(prev => prev.map(t =>
          t.id === taskId ? { ...t, status, result: task.result, error: task.error, completedAt: task.completedAt } : t
        ));

        if (status === 'completed') {
          toast.success(`${action.name} completed`);
          return;
        }
        if (status === 'failed' || status === 'cancelled') {
          toast.error(`${action.name} failed`);
          return;
        }
      } catch (err: any) {
        // Stop polling if task was deleted (404)
        if (err.response?.status === 404) {
          setRecentTasks(prev => prev.filter(t => t.id !== taskId));
          return;
        }
        // Otherwise keep polling
      }
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'failed': return <XCircleIcon className="h-5 w-5 text-red-500" />;
      case 'queued': case 'processing': return <ClockIcon className="h-5 w-5 text-amber-500 animate-pulse" />;
      default: return <ClockIcon className="h-5 w-5 text-content-subtle" />;
    }
  };

  const getActionByAgent = (agentType: string) => ACTIONS.find(a => a.agent === agentType);

  const formatElapsed = (startedAt: string | undefined, nowMs: number): string => {
    if (!startedAt) return '0s';
    const secs = Math.max(0, Math.round((nowMs - new Date(startedAt).getTime()) / 1000));
    if (secs < 60) return `${secs}s`;
    return `${Math.floor(secs / 60)}m ${String(secs % 60).padStart(2, '0')}s`;
  };

  const formatTime = (dateStr: string) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMin = Math.round((now.getTime() - d.getTime()) / 60000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffMin < 1440) return `${Math.round(diffMin / 60)}h ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };


  return (
    <div className="space-y-6">
      {/* Engine outage banner. Without this an outage is only discoverable by
          starting a task and waiting for it to time out, which is how the
          2026-08-19 harness wedge was found. */}
      {healthStatus === 'unavailable' && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-sm font-semibold text-red-800">AI services are temporarily unavailable</p>
          <p className="text-sm text-red-700 mt-1">
            Agent actions won't run right now, and starting one will fail rather than wait.
            No credits are used while service is down. Everything else in Clevor keeps working.
          </p>
        </div>
      )}
      {healthStatus === 'degraded' && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <p className="text-sm font-semibold text-amber-800">AI services are running slowly</p>
          <p className="text-sm text-amber-700 mt-1">
            Agent actions may take longer than usual or time out.
          </p>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <PageHeader
        title="AI Actions"
        subtitle="Run AI-powered analysis on your pipeline opportunities"
      />
        <div className="flex items-center gap-3">
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${
            healthStatus === 'healthy' ? 'bg-green-100 text-green-700' :
            healthStatus === 'ui_only' ? 'bg-blue-100 text-blue-700' :
            healthStatus === 'degraded' ? 'bg-amber-100 text-amber-700' :
            healthStatus === 'checking' ? 'bg-surface-sunk text-content-muted' :
            'bg-red-100 text-red-700'
          }`}>
            <span className={`w-2 h-2 rounded-full ${
              healthStatus === 'healthy' ? 'bg-green-500' :
              healthStatus === 'ui_only' ? 'bg-blue-500' :
              healthStatus === 'degraded' ? 'bg-amber-500' :
              'bg-red-500'
            }`} />
            {healthStatus === 'healthy' ? 'AI Online' :
             healthStatus === 'ui_only' ? 'Limited Mode' :
             healthStatus === 'degraded' ? 'Degraded' :
             healthStatus === 'checking' ? 'Checking...' :
             'AI Offline'}
          </div>
          <button onClick={() => { fetchRecentTasks(); checkHealth(); }} className="p-2 text-content-subtle hover:text-content-muted hover:bg-surface-sunk rounded-lg">
            <ArrowPathIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Opportunity Selector */}
      <div className="bg-surface shadow rounded-lg p-5">
        <label className="block text-sm font-medium text-content mb-2">Select Opportunity from Pipeline</label>
        {loadingOpps ? (
          <div className="text-sm text-content-subtle">Loading pipeline...</div>
        ) : opportunities.length === 0 ? (
          <div className="text-sm text-content-muted">
            No opportunities in your pipeline.{' '}
            <Link to="/opportunities" className="text-brand-600 hover:underline">Find opportunities</Link> and add them to your pipeline first.
          </div>
        ) : (
          <select
            value={selectedOpp?.id || ''}
            onChange={(e) => {
              const opp = opportunities.find(o => o.id === e.target.value);
              setSelectedOpp(opp || null);
            }}
            className="input w-full"
          >
            {opportunities.map(opp => (
              <option key={opp.id} value={opp.id}>
                {opp.opportunity?.title || 'Untitled'} — {opp.opportunity?.agency || 'Unknown'} {opp.opportunity?.solicitationNumber ? `(${opp.opportunity.solicitationNumber})` : ''}
              </option>
            ))}
          </select>
        )}
        {selectedOpp?.opportunity && (
          <div className="mt-2 flex items-center gap-3 text-xs text-content-muted">
            <span>Stage: <span className="font-medium capitalize">{selectedOpp.stage}</span></span>
            <span>Match: <span className="font-medium">{Math.round(selectedOpp.matchScore * 100)}%</span></span>
            {selectedOpp.opportunity.solicitationNumber && (
              <span>Sol: <span className="font-mono">{selectedOpp.opportunity.solicitationNumber}</span></span>
            )}
            <Link to={`/opportunities/${selectedOpp.opportunity.samNoticeId || selectedOpp.opportunityId}`} className="text-brand-600 hover:underline ml-auto">
              View Details
            </Link>
          </div>
        )}
      </div>

      {/* Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {ACTIONS.map(action => {
          const isRunning = runningAction === action.id;
          const disabled = !selectedOpp || isRunning || healthStatus === 'unavailable';

          return (
            <button
              key={action.id}
              onClick={() => runAction(action)}
              disabled={disabled}
              className={`text-left p-5 rounded-lg border-2 transition-all ${
                disabled
                  ? 'border-border bg-surface-sunk opacity-60 cursor-not-allowed'
                  : 'border-border bg-surface hover:border-brand-300 hover:shadow-md cursor-pointer'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className={`p-2.5 rounded-lg ${action.bgLight}`}>
                  <action.icon className={`h-6 w-6 ${action.textColor}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-content text-sm">{action.name}</h3>
                  <p className="text-xs text-content-muted mt-1 leading-relaxed">{action.description}</p>
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between">
                <span className="text-xs text-content-subtle tabular-nums">1 credit</span>
                {isRunning ? (
                  <span className="flex items-center gap-1.5 text-xs text-amber-600">
                    <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
                    Running...
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5 text-xs text-brand-600 font-medium">
                    <PlayIcon className="h-3.5 w-3.5" />
                    Run
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Recent Results — Grouped by Opportunity */}
      {(() => {
        // Group tasks by opportunity, deduplicate to latest per agent type
        const grouped = recentTasks.reduce<Record<string, { title: string; tasks: TaskResult[] }>>((acc, task) => {
          const key = task.opportunityTitle || 'Other';
          if (!acc[key]) acc[key] = { title: key, tasks: [] };
          // Only keep latest per agent type (tasks are already sorted by createdAt desc)
          const existing = acc[key].tasks.find(t => t.agentType === task.agentType && t.status === 'completed');
          if (task.status !== 'completed' || !existing) {
            acc[key].tasks.push(task);
          }
          return acc;
        }, {});

        const groupKeys = Object.keys(grouped).sort((a, b) => {
          const aLatest = Math.max(...grouped[a].tasks.map(t => new Date(t.createdAt).getTime()));
          const bLatest = Math.max(...grouped[b].tasks.map(t => new Date(t.createdAt).getTime()));
          return bLatest - aLatest;
        });

        const totalTasks = recentTasks.length;
        const failedTasks = recentTasks.filter(t => t.status === 'failed');

        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-title text-content">Recent Results</h2>
              <div className="flex items-center gap-3">
                {failedTasks.length > 0 && (
                  <button
                    onClick={async () => {
                      try {
                        await agentApi.clearFailedTasks();
                        setRecentTasks(prev => prev.filter(t => t.status !== 'failed' && t.status !== 'cancelled'));
                        toast.success('Failed tasks cleared');
                      } catch {
                        toast.error('Failed to clear tasks');
                      }
                    }}
                    className="text-xs text-red-500 hover:text-red-700"
                  >
                    Clear failed ({failedTasks.length})
                  </button>
                )}
                <span className="text-sm text-content-subtle">
                  {totalTasks >= RECENT_TASK_LIMIT ? `Last ${RECENT_TASK_LIMIT} runs` : `${totalTasks} run${totalTasks !== 1 ? 's' : ''}`}
                  {' · '}{groupKeys.length} pursuit{groupKeys.length !== 1 ? 's' : ''}
                </span>
              </div>
            </div>

            {loadingTasks ? (
              <div className="bg-surface shadow rounded-lg px-6 py-8 text-center text-content-subtle">
                <ArrowPathIcon className="h-6 w-6 animate-spin mx-auto mb-2" />
                Loading...
              </div>
            ) : totalTasks === 0 ? (
              <div className="bg-surface shadow rounded-lg px-6 py-8 text-center text-content-muted">
                <SparklesIcon className="h-10 w-10 mx-auto text-content-subtle mb-3" />
                <p>No results yet. Select an opportunity and run an action to get started.</p>
              </div>
            ) : (
              groupKeys.map(groupKey => {
                const group = grouped[groupKey];
                const isGroupCollapsed = collapsedGroups.has(groupKey);
                const completedCount = group.tasks.filter(t => t.status === 'completed').length;
                const runningCount = group.tasks.filter(t => t.status === 'queued' || t.status === 'processing').length;

                return (
                  <div key={groupKey} className="bg-surface shadow rounded-lg overflow-hidden">
                    {/* Group Header */}
                    <button
                      onClick={() => {
                        setCollapsedGroups(prev => {
                          const next = new Set(prev);
                          if (next.has(groupKey)) next.delete(groupKey);
                          else next.add(groupKey);
                          return next;
                        });
                      }}
                      className="w-full px-6 py-3 flex items-center justify-between hover:bg-surface-sunk transition-colors"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <SparklesIcon className="h-5 w-5 text-brand-500 flex-shrink-0" />
                        <div className="text-left min-w-0">
                          <p className="text-sm font-medium text-content truncate">{groupKey}</p>
                          <div className="flex items-center gap-2 text-xs text-content-muted mt-0.5">
                            <span>{group.tasks.length} action{group.tasks.length !== 1 ? 's' : ''}</span>
                            {completedCount > 0 && <span className="text-green-600">{completedCount} completed</span>}
                            {runningCount > 0 && <span className="text-amber-600">{runningCount} running</span>}
                          </div>
                        </div>
                      </div>
                      {isGroupCollapsed
                        ? <ChevronRightIcon className="h-4 w-4 text-content-subtle flex-shrink-0" />
                        : <ChevronDownIcon className="h-4 w-4 text-content-subtle flex-shrink-0" />}
                    </button>

                    {/* Task List */}
                    {!isGroupCollapsed && (
                      <div className="border-t border-border divide-y divide-gray-50">
                        {group.tasks.map(task => {
                          const action = getActionByAgent(task.agentType);
                          const isExpanded = expandedResult === task.id;

                          return (
                            <div key={task.id}>
                              <button
                                onClick={() => setExpandedResult(isExpanded ? null : task.id)}
                                className="w-full px-6 py-3 hover:bg-surface-sunk transition-colors text-left"
                              >
                                <div className="flex items-center gap-3">
                                  {getStatusIcon(task.status)}
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm font-medium text-content">
                                        {action?.name || task.agentType}
                                      </span>
                                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                                        task.status === 'completed' ? 'bg-green-100 text-green-700' :
                                        task.status === 'failed' ? 'bg-red-100 text-red-700' :
                                        'bg-amber-100 text-amber-700'
                                      }`}>
                                        {task.status}
                                      </span>
                                    </div>
                                  </div>
                                  <span className="text-xs text-content-subtle">{formatTime(task.createdAt)}</span>
                                  {(task.status === 'completed' || task.status === 'failed') && (
                                    isExpanded
                                      ? <ChevronDownIcon className="h-4 w-4 text-content-subtle flex-shrink-0" />
                                      : <ChevronRightIcon className="h-4 w-4 text-content-subtle flex-shrink-0" />
                                  )}
                                </div>

                                {/* Waiting state.
                                    This used to be a bar hardcoded to 15% queued / 60%
                                    processing — a position with nothing behind it, which is
                                    why it always stalled just past the middle. There is no
                                    progress signal to read: the worker reports queued or
                                    done and nothing in between. So show elapsed time against
                                    a typical duration, and an indeterminate bar that claims
                                    activity rather than position. */}
                                {(task.status === 'queued' || task.status === 'processing') && (
                                  <div className="mt-2 ml-8">
                                    <div className="flex items-center gap-3">
                                      <div className="flex-1 bg-gray-200 rounded-full h-1.5 overflow-hidden">
                                        <div className="h-full w-full rounded-full bg-brand-500 animate-pulse" />
                                      </div>
                                      <span className="text-xs text-content-subtle flex-shrink-0 tabular-nums">
                                        {task.status === 'queued'
                                          ? 'Queued'
                                          : `${formatElapsed(task.startedAt || task.createdAt, now)} elapsed`}
                                      </span>
                                    </div>
                                    {task.status === 'processing' && (
                                      <p className="text-[11px] text-content-muted mt-1">
                                        {TYPICAL_RUNTIME[task.agentType] || 'Usually takes 1–3 minutes'} — you can
                                        leave this page, it keeps running.
                                      </p>
                                    )}
                                  </div>
                                )}
                              </button>

                              {isExpanded && task.result && (
                                <div className="px-6 pb-3">
                                  <div className="bg-surface-sunk rounded-lg p-4 text-sm text-content max-h-96 overflow-y-auto border border-border">
                                    <AgentResultView result={task.result} />
                                  </div>

                                  {/* Quick follow-up actions after analysis */}
                                  {task.status === 'completed' && task.agentType === 'contract-scout' && (
                                    <div className="mt-3 p-3 bg-brand-50 border border-brand-200 rounded-lg">
                                      <p className="text-xs font-medium text-brand-800 mb-2">Recommended next steps:</p>
                                      <div className="flex flex-wrap gap-2">
                                        <button
                                          onClick={() => {
                                            const opp = opportunities.find(o => o.id === task.payload?.opportunity_id);
                                            if (opp) {
                                              setSelectedOpp(opp);
                                              const complianceAction = ACTIONS.find(a => a.id === 'compliance');
                                              if (complianceAction) runAction(complianceAction);
                                            }
                                          }}
                                          className="text-xs px-3 py-1.5 bg-green-100 text-green-700 hover:bg-green-200 rounded-lg font-medium transition-colors"
                                        >
                                          Run Compliance Check
                                        </button>
                                        <button
                                          onClick={() => {
                                            const opp = opportunities.find(o => o.id === task.payload?.opportunity_id);
                                            if (opp) {
                                              setSelectedOpp(opp);
                                              const vendorAction = ACTIONS.find(a => a.id === 'vendor-match');
                                              if (vendorAction) runAction(vendorAction);
                                            }
                                          }}
                                          className="text-xs px-3 py-1.5 bg-purple-100 text-purple-700 hover:bg-purple-200 rounded-lg font-medium transition-colors"
                                        >
                                          Find Teaming Partners
                                        </button>
                                        <button
                                          onClick={() => {
                                            const opp = opportunities.find(o => o.id === task.payload?.opportunity_id);
                                            if (opp) {
                                              setSelectedOpp(opp);
                                              const monitorAction = ACTIONS.find(a => a.id === 'monitor');
                                              if (monitorAction) runAction(monitorAction);
                                            }
                                          }}
                                          className="text-xs px-3 py-1.5 bg-amber-100 text-amber-700 hover:bg-amber-200 rounded-lg font-medium transition-colors"
                                        >
                                          Monitor Changes
                                        </button>
                                        <Link
                                          to={`/proposal-writer?opportunityId=${task.payload?.opportunity_id}`}
                                          className="text-xs px-3 py-1.5 bg-brand-100 text-brand-700 hover:bg-brand-200 rounded-lg font-medium transition-colors"
                                        >
                                          Write Proposal
                                        </Link>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}

                              {isExpanded && task.error && (
                                <div className="px-6 pb-3">
                                  <div className="bg-red-50 rounded-lg p-4 text-sm text-red-700 border border-red-200">
                                    {task.error.length > 200 ? task.error.substring(0, 200) + '...' : task.error}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        );
      })()}
    </div>
  );
};

export default Agents;
